// client/src/pages/RedeemPage.jsx
// Public voucher-redemption page. No auth — a student opens
// tictac.matara.pro/redeem on any browser, types their card number +
// the printed voucher's 9-digit code + the amount printed on the
// voucher. Server credits the student's camp account and returns the
// new balance.
//
// The amount field acts as a second factor: someone who overhears the
// code can't redeem without seeing the physical voucher. The server
// returns the same generic redemption_failed code for "amount
// mismatch" as for "code not found" so a brute-forcer can't tell
// which factor they got wrong.
//
// The page lives outside the back-office Layout. It's reachable
// from ANY origin / auth state, so the markup deliberately doesn't
// touch useMe / useLogout / anything that assumes a session.

import { useState } from 'react';
import Logo from '../components/Logo.jsx';
import LogoWordmark from '../components/LogoWordmark.jsx';
import VersionFooter from '../components/VersionFooter.jsx';
import { ApiError } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';
import { formatNumber } from '../lib/currency.js';
import { ExclamationTriangleIcon } from '../components/Icons.jsx';

export default function RedeemPage() {
  const [cardNumber, setCardNumber]   = useState('');
  const [code, setCode]               = useState('');
  const [amount, setAmount]           = useState('');
  const [busy, setBusy]               = useState(false);
  const [inquiryBusy, setInquiryBusy] = useState(false);
  const [topError, setTopError]       = useState(null);
  const [result, setResult]           = useState(null);
  const [inquiry, setInquiry]         = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setTopError(null);
    try {
      const res = await fetch('/api/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardNumber: cardNumber.replace(/\D/g, ''),
          code:       code.replace(/\D/g, ''),
        }),
      });
      const text = await res.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { /* leave null */ }
      if (!res.ok) {
        throw new ApiError({
          status:  res.status,
          code:    payload?.error,
          message: payload?.message,
          details: payload?.details,
        });
      }
      setResult(payload);
    } catch (err) {
      if (err instanceof ApiError) {
        // Both 'voucher_not_found' and 'amount mismatch' come back as
        // the same `redemption_failed` code from the service — by
        // design. The message field carries the specific reason but
        // we map several of them to a single generic error so a
        // brute-forcer learns nothing.
        if (err.code === 'too_many_attempts') {
          const mins = err.details?.retryAfterMinutes;
          const waitTxt = !mins ? 'מאוחר יותר' : mins === 1 ? 'בעוד דקה' : `בעוד ${mins} דקות`;
          setTopError(`בוצעו יותר מדי נסיונות שגויים. נסו שוב ${waitTxt}.`);
        } else if (err.code === 'redemption_failed') {
          const msg = (err.message || '').toLowerCase();
          if (msg.includes('expired')) setTopError('השובר פג תוקף.');
          else if (msg.includes('cancelled')) setTopError('השובר בוטל. פני למורה.');
          else if (msg.includes('already_redeemed')) setTopError('השובר כבר מומש בעבר.');
          else if (msg.includes('unknown_card')) setTopError('מספר הכרטיס אינו רשום במערכת.');
          else setTopError('קוד שגוי. בדוק את הקוד על השובר ונסה שוב.');
        } else if (err.status === 422 && Array.isArray(err.details)) {
          const first = err.details[0];
          if (first?.message === 'card_must_be_16_digits') setTopError('מספר הכרטיס חייב להיות 16 ספרות.');
          else if (first?.message === 'code_must_be_6_to_9_digits') setTopError('קוד השובר אינו תקין.');
          else setTopError('יש לבדוק את הפרטים שהוקלדו.');
        } else if (err.status === 429) {
          setTopError('יותר מדי ניסיונות. נסה שוב בעוד מספר דקות.');
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleInquiry() {
    const card = cardNumber.replace(/\D/g, '');
    if (card.length !== 16) {
      setTopError('כדי לבדוק יתרה צריך להזין את 16 ספרות הכרטיס.');
      return;
    }
    setInquiryBusy(true);
    setTopError(null);
    try {
      const res = await fetch('/api/redeem/inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardNumber: card }),
      });
      const text = await res.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { /* leave null */ }
      if (!res.ok) {
        throw new ApiError({
          status:  res.status,
          code:    payload?.error,
          message: payload?.message,
          details: payload?.details,
        });
      }
      setInquiry(payload);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'inquiry_failed' || err.status === 404) {
          setTopError('מספר הכרטיס אינו רשום במערכת.');
        } else if (err.status === 429) {
          setTopError('יותר מדי ניסיונות. נסה שוב בעוד מספר דקות.');
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setInquiryBusy(false);
    }
  }

  if (inquiry) return <InquiryScreen result={inquiry} onBack={() => setInquiry(null)} />;

  if (result) return <SuccessScreen result={result} onAnother={() => {
    setResult(null);
    setCode('');
    setAmount('');
    // Keep cardNumber so siblings sharing a phone don't retype.
  }} />;

  return (
    <div className="min-h-screen flex flex-col">
      <RedeemHeader />
      <main className="flex-1 px-4 py-8 sm:py-12">
        <div className="max-w-md mx-auto animate-fade-in">
          <div className="card-pop p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-2 bg-brand-gradient" />
            <Logo className="mx-auto block w-full max-w-[220px] h-auto" />

            <h1 className="h-page mt-3 text-center">מימוש שובר</h1>
            <p className="mt-2 text-muted text-center">
              הקלד את מספר הכרטיס שלך, את קוד השובר, ואת מספר הנקודות הרשום
              עליו. הנקודות יתווספו ליתרה מיד.
            </p>

            {topError && (
              <div
                role="alert"
                className="mt-5 rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up"
              >
                <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{topError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="label">מספר כרטיס (16 ספרות)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="input input-lg font-mono num tracking-wider"
                  dir="ltr"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                  maxLength={19}
                  autoFocus
                  required
                />
              </label>

              <label className="block">
                <span className="label">קוד השובר (9 ספרות)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="input input-lg font-mono num tracking-[0.18em]"
                  dir="ltr"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  maxLength={11}
                  required
                />
              </label>

              <button
                type="submit"
                className="btn-pop btn-lg w-full justify-center"
                disabled={busy}
              >
                {busy ? 'מבצע...' : 'מימוש'}
              </button>
            </form>

            <div className="mt-4 pt-4 border-t border-ink-100">
              <button
                type="button"
                onClick={handleInquiry}
                disabled={inquiryBusy || busy}
                className="btn-secondary w-full justify-center"
              >
                {inquiryBusy ? 'בודק...' : 'בירור יתרה ופעולות אחרונות'}
              </button>
              <p className="mt-2 text-xs text-ink-500 text-center">
                בלי שובר — רק מספר הכרטיס.
              </p>
            </div>

            <p className="mt-5 text-xs text-ink-500 text-center">
              לא קיבלת שובר? פנה למורה שלך.
            </p>
          </div>
        </div>
      </main>
      <VersionFooter />
    </div>
  );
}

function RedeemHeader() {
  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-ink-200/70">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center">
        <a href="/" aria-label="TicTac · עמוד הבית" className="block shrink-0">
          <LogoWordmark className="h-10 w-auto" />
        </a>
      </div>
    </header>
  );
}

function InquiryScreen({ result, onBack }) {
  const { student, balance, transactions } = result;
  return (
    <div className="min-h-screen flex flex-col">
      <RedeemHeader />
      <main className="flex-1 px-4 py-8 sm:py-12">
        <div className="max-w-md mx-auto animate-fade-in space-y-4">
          <div className="card-pop p-7 sm:p-9 text-center relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-2 bg-brand-gradient" />
            <h1 className="font-display font-extrabold text-3xl mt-3 text-ink-900">
              בירור יתרה
            </h1>
            <p className="mt-2 text-muted">
              שלום <strong className="text-ink-900">{student.firstName} {student.lastName}</strong>
              {student.className && <span> · {student.className}</span>}
            </p>

            <div className="mt-5 rounded-2xl bg-brand-50 border border-brand-200 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-brand-700">היתרה שלך</div>
              <div className="mt-1 font-display font-extrabold text-5xl text-brand-800 num leading-none">
                {formatNumber(balance)}
              </div>
              <div className="mt-1 text-xs text-brand-700">נקודות</div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="px-5 py-3 border-b border-ink-100 bg-ink-50/50">
              <div className="text-sm font-bold text-ink-900">פעולות אחרונות</div>
              <div className="text-xs text-ink-500 mt-0.5">10 העסקאות האחרונות בכרטיסך</div>
            </div>
            {transactions.length === 0 ? (
              <div className="p-6 text-center text-sm text-ink-500">
                אין עדיין פעולות בכרטיס.
              </div>
            ) : (
              <ul className="divide-y divide-ink-100">
                {transactions.map((t) => <InquiryRow key={t.id} t={t} />)}
              </ul>
            )}
          </div>

          <button
            type="button"
            className="btn-secondary w-full justify-center"
            onClick={onBack}
          >
            חזרה
          </button>
        </div>
      </main>
      <VersionFooter />
    </div>
  );
}

function InquiryRow({ t }) {
  const isCredit = t.direction === 'CREDIT';
  return (
    <li className="px-5 py-3 flex items-center gap-3">
      <div
        className={`w-1.5 self-stretch rounded-full ${isCredit ? 'bg-mint-500' : 'bg-sky-500'}`}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink-900 truncate">{t.itemLabel}</div>
        <div className="text-xs text-ink-500 mt-0.5 num truncate">
          {formatDateTime(t.createdAt)}
          {t.device?.name && <> · {t.device.name}</>}
        </div>
      </div>
      <div className={`font-bold num text-base shrink-0 ${isCredit ? 'text-mint-700' : 'text-sky-700'}`}>
        {isCredit ? '+' : '−'}{formatNumber(t.amount)}
      </div>
    </li>
  );
}

function SuccessScreen({ result, onAnother }) {
  const { student, voucher, balance } = result;
  return (
    <div className="min-h-screen flex flex-col">
      <RedeemHeader />
      <main className="flex-1 px-4 py-8 sm:py-12">
        <div className="max-w-md mx-auto animate-fade-in">
          <div className="card-pop p-7 sm:p-9 text-center relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-2 bg-mint-gradient" />
            <div className="mx-auto w-20 h-20 rounded-3xl bg-mint-gradient text-white grid place-items-center shadow-pop mt-3">
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h1 className="font-display font-extrabold text-3xl mt-4 text-ink-900">
              נוספו לך {formatNumber(voucher.amount)} נקודות!
            </h1>
            <p className="mt-2 text-muted">
              שלום <strong className="text-ink-900">{student.firstName} {student.lastName}</strong>
              {student.className && <span> · {student.className}</span>}
            </p>

            <div className="mt-5 rounded-2xl bg-mint-50 border border-mint-200 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-mint-700">היתרה החדשה שלך</div>
              <div className="mt-1 font-display font-extrabold text-5xl text-mint-800 num leading-none">
                {formatNumber(balance)}
              </div>
              <div className="mt-1 text-xs text-mint-700">נקודות</div>
            </div>

            <p className="mt-5 text-xs text-ink-500 num">
              מספר השובר: <span dir="ltr" className="font-mono">{voucher.code}</span>
            </p>

            <button
              type="button"
              className="btn-secondary w-full justify-center mt-5"
              onClick={onAnother}
            >
              מימוש שובר נוסף
            </button>
          </div>
        </div>
      </main>
      <VersionFooter />
    </div>
  );
}
